<template>
  <div class="tab-shell">
    <!-- ===== LISTA ===== -->
    <template v-if="mode === 'list'">
      <div class="tab-head">
        <h2 class="tab-title">Requisições de Compra</h2>
        <div class="tab-head-actions">
          <button class="btn-secondary" type="button" :disabled="exporting" @click="exportToExcel">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {{ exporting ? 'Exportando...' : 'Exportar' }}
          </button>
          <button class="btn-primary" type="button" @click="openForm()">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nova RC
          </button>
        </div>
      </div>

      <!-- Filter tabs -->
      <div class="filter-tabs">
        <button v-for="f in statusFilters" :key="f.value" class="filter-tab" :class="{ active: statusFilter === f.value }" type="button" @click="statusFilter = f.value">{{ f.label }}</button>
      </div>

      <div v-if="loading" class="empty-hint">Carregando...</div>
      <div v-else-if="filteredRcs.length === 0" class="empty-hint">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        Nenhuma requisição encontrada.
      </div>
      <div v-else class="cards-grid">
        <article v-for="rc in filteredRcs" :key="rc.id" class="rc-card" role="button" tabindex="0" @click="openView(rc)" @keydown.enter="openView(rc)">
          <div class="card-body">
            <div class="card-header-row">
              <span class="card-title">{{ rc.titulo }}</span>
              <span class="urgencia-badge" :class="`urgencia--${rc.urgencia}`">{{ rc.urgencia }}</span>
            </div>
            <div class="card-meta">{{ formatDate(rc.createdAt) }}<span v-if="rc.projectName"> · {{ rc.projectName }}</span></div>
            <div class="card-meta" v-if="rc.fornecedorNome">Fornecedor: {{ rc.fornecedorNome }}</div>
            <p v-if="rc.justificativa" class="card-justificativa">{{ rc.justificativa }}</p>
            <div class="card-row">
              <span class="status-chip" :class="rcStatusClass(rc.status)">{{ rcStatusLabel(rc.status) }}</span>
              <span v-if="rc.sourceSubmissionId" class="origin-chip" title="Gerada automaticamente a partir de um relatório de campo">via relatório</span>
              <span v-if="rc.itemCount" class="card-meta">{{ rc.itemCount }} iten(s)</span>
              <span v-if="rc.valorTotal" class="card-value">{{ formatCurrency(rc.valorTotal) }}</span>
            </div>
          </div>
          <div class="card-footer" @click.stop>
            <button class="card-btn card-btn--primary" type="button" @click="openView(rc)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Visualizar
            </button>
            <button v-if="canEdit(rc)" class="card-btn" type="button" @click="openForm(rc)">Editar</button>
            <button v-if="rc.status === 'rascunho'" class="card-btn card-btn--primary" type="button" @click="submit(rc.id)">Enviar para aprovação</button>
            <button v-if="rc.status === 'pendente'" class="card-btn card-btn--ok" type="button" @click="respond(rc.id, 'approve')">Aprovar</button>
            <button v-if="rc.status === 'pendente'" class="card-btn card-btn--warn" type="button" @click="respond(rc.id, 'reject')">Reprovar</button>
          </div>
        </article>
      </div>
    </template>

    <!-- ===== VISUALIZAR (tela cheia) ===== -->
    <div v-else-if="mode === 'view' && viewRc" class="page-shell">
      <div class="page-topbar">
        <button class="btn-back" type="button" @click="backToList">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Voltar
        </button>
        <div class="page-topbar-actions">
          <button v-if="canEdit(viewRc)" class="btn-secondary" type="button" @click="openForm(viewRc)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Editar
          </button>
          <button v-if="viewRc.status === 'rascunho'" class="btn-primary" type="button" @click="submit(viewRc.id)">Enviar para aprovação</button>
          <button v-if="viewRc.status === 'pendente'" class="btn-approve" type="button" @click="respond(viewRc.id, 'approve')">Aprovar</button>
          <button v-if="viewRc.status === 'pendente'" class="btn-reject" type="button" @click="respond(viewRc.id, 'reject')">Reprovar</button>
        </div>
      </div>

      <div class="page-card">
        <div class="view-header">
          <h2 class="view-title">{{ viewRc.titulo }}</h2>
          <div class="view-chips">
            <span class="status-chip" :class="rcStatusClass(viewRc.status)">{{ rcStatusLabel(viewRc.status) }}</span>
            <span class="urgencia-badge" :class="`urgencia--${viewRc.urgencia}`">{{ viewRc.urgencia }}</span>
            <span v-if="viewRc.sourceSubmissionId" class="origin-chip" title="Gerada automaticamente a partir de um relatório de campo">via relatório</span>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-block">
            <span class="meta-label">Data de criação</span>
            <span class="meta-value">{{ formatDate(viewRc.createdAt) }}</span>
          </div>
          <div class="meta-block">
            <span class="meta-label">Obra</span>
            <span class="meta-value">{{ viewRc.projectName || '—' }}</span>
          </div>
          <div class="meta-block">
            <span class="meta-label">Fornecedor sugerido</span>
            <span class="meta-value">{{ viewRc.fornecedorNome || '—' }}</span>
          </div>
          <div class="meta-block">
            <span class="meta-label">Solicitante</span>
            <span class="meta-value">{{ viewRc.requestedBy || '—' }}</span>
          </div>
          <div class="meta-block">
            <span class="meta-label">Valor total estimado</span>
            <span class="meta-value meta-value--strong">{{ viewItemsTotal ? formatCurrency(viewItemsTotal) : '—' }}</span>
          </div>
        </div>

        <div v-if="viewRc.justificativa" class="view-section">
          <h3 class="view-section-title">Justificativa</h3>
          <p class="view-text">{{ viewRc.justificativa }}</p>
        </div>

        <div class="view-section">
          <h3 class="view-section-title">Itens requisitados</h3>
          <div v-if="viewLoading" class="items-empty-hint">Carregando itens...</div>
          <div v-else-if="viewItems.length === 0" class="items-empty-hint">Nenhum item nesta requisição.</div>
          <div v-else class="items-table-wrap">
            <table class="items-table">
              <thead>
                <tr>
                  <th class="col-img"></th>
                  <th>Item</th>
                  <th>Descrição</th>
                  <th>Link</th>
                  <th class="col-num">Qtd</th>
                  <th class="col-num">Un.</th>
                  <th class="col-num">Valor unit. est.</th>
                  <th class="col-num">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in viewItems" :key="idx">
                  <td class="col-img">
                    <img v-if="item.imageUrl" :src="resolveUrl(item.imageUrl)" class="item-thumb" alt="" @click="openImage(item.imageUrl)" />
                  </td>
                  <td class="cell-strong">{{ item.nome || item.descricao || '—' }}</td>
                  <td>{{ item.nome ? (item.descricao || '—') : '—' }}</td>
                  <td>
                    <a v-if="item.link" :href="item.link" target="_blank" rel="noopener" class="item-link">abrir</a>
                    <span v-else>—</span>
                  </td>
                  <td class="col-num">{{ item.quantidade }}</td>
                  <td class="col-num">{{ item.unidade || 'un' }}</td>
                  <td class="col-num">{{ item.valorUnitarioEstimado ? formatCurrency(item.valorUnitarioEstimado) : '—' }}</td>
                  <td class="col-num cell-strong">{{ item.valorUnitarioEstimado ? formatCurrency(item.valorUnitarioEstimado * (item.quantidade || 1)) : '—' }}</td>
                </tr>
              </tbody>
              <tfoot v-if="viewItemsTotal">
                <tr>
                  <td colspan="7" class="col-num cell-strong">Total estimado</td>
                  <td class="col-num cell-strong">{{ formatCurrency(viewItemsTotal) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== FORMULÁRIO (tela cheia) ===== -->
    <div v-else class="page-shell">
      <div class="page-topbar">
        <button class="btn-back" type="button" @click="closeForm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Voltar
        </button>
      </div>

      <div class="page-card">
        <h2 class="view-title form-page-title">{{ editId ? 'Editar Requisição' : 'Nova Requisição de Compra' }}</h2>
        <div class="form-grid">
          <label class="field field--full">
            <span class="field-label">Título *</span>
            <input class="field-input" v-model="form.titulo" placeholder="Descreva brevemente a necessidade" />
          </label>
          <label class="field field--full">
            <span class="field-label">Justificativa</span>
            <textarea class="field-input field-textarea" v-model="form.justificativa" rows="2" placeholder="Por que esta compra é necessária?"></textarea>
          </label>
          <label class="field">
            <span class="field-label">Urgência</span>
            <select class="field-input" v-model="form.urgencia">
              <option value="normal">Normal</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Obra</span>
            <select class="field-input" v-model="form.projectId">
              <option value="">Selecione...</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </label>
          <label class="field field--full">
            <span class="field-label">Fornecedor Sugerido</span>
            <select class="field-input" v-model="form.fornecedorId">
              <option value="">Nenhum</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.nomeFantasia || s.razaoSocial }}</option>
            </select>
          </label>
        </div>

        <!-- Items -->
        <div class="items-section">
          <div class="items-header">
            <span class="items-title">Itens</span>
            <button class="btn-add-item" type="button" @click="addItem">+ Adicionar item</button>
          </div>
          <div v-if="formItemsLoading" class="items-empty-hint">Carregando itens...</div>
          <template v-else>
            <div v-if="form.items.length > 0" class="item-row item-row--head">
              <span>Item</span><span>Descrição</span><span>Link</span><span>Qtd</span><span>Un.</span><span>Valor est.</span><span></span>
            </div>
            <div v-for="(item, idx) in form.items" :key="idx" class="item-row">
              <input class="field-input item-desc" v-model="item.nome" placeholder="Nome do item" />
              <input class="field-input item-desc" v-model="item.descricao" placeholder="Descrição / detalhes" />
              <input class="field-input item-desc" v-model="item.link" placeholder="Link (opcional)" />
              <input class="field-input item-qty" type="number" v-model.number="item.quantidade" min="1" placeholder="Qtd" />
              <input class="field-input item-unit" v-model="item.unidade" placeholder="Un." />
              <input class="field-input item-value" type="number" v-model.number="item.valorUnitarioEstimado" min="0" step="0.01" placeholder="Valor est." />
              <button class="btn-remove-item" type="button" @click="removeItem(idx)">✕</button>
            </div>
            <div v-if="form.items.length === 0" class="items-empty-hint">Nenhum item adicionado.</div>
          </template>
        </div>

        <p v-if="formError" class="form-error">{{ formError }}</p>
        <div class="form-actions">
          <button class="btn-save" type="button" :disabled="saving" @click="save">{{ saving ? 'Salvando...' : (editId ? 'Salvar Alterações' : 'Salvar Rascunho') }}</button>
          <button class="btn-cancel" type="button" @click="closeForm">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- Lightbox simples para foto do item -->
    <div v-if="lightboxUrl" class="lightbox" @click="lightboxUrl = ''">
      <img :src="lightboxUrl" alt="" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import * as XLSX from 'xlsx'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { projectsStore } from '@/store/projects'
import { resolveUrl } from '@/utils/resolveUrl'

interface Supplier { id: string; razaoSocial: string; nomeFantasia: string }
interface RcItem {
  nome: string; descricao: string; link: string; imageUrl: string
  quantidade: number; unidade: string; valorUnitarioEstimado: number; warehouseItemId?: string
}
interface Rc {
  id: string; titulo: string; justificativa: string; urgencia: string; status: string
  projectId: string; projectName: string; fornecedorId: string; fornecedorNome: string
  itemCount: number; valorTotal: number; createdAt: string; sourceSubmissionId?: string
  requestedBy?: string
}
interface RcExportItem {
  nome: string; descricao: string; link: string; quantidade: number; unidade: string
}
interface RcExport extends Rc {
  requestedByName: string
  items: RcExportItem[]
}

const emptyForm = () => ({
  titulo: '', justificativa: '', urgencia: 'normal', projectId: '', fornecedorId: '',
  items: [] as RcItem[]
})
const RC_STATUS_LABELS: Record<string, string> = {
  rascunho: 'Rascunho', pendente: 'Pendente', aprovada: 'Aprovada',
  reprovada: 'Reprovada', concluida: 'Concluída'
}
const RC_STATUS_CLASS: Record<string, string> = {
  rascunho: 'chip--draft', pendente: 'chip--yellow', aprovada: 'chip--green',
  reprovada: 'chip--red', concluida: 'chip--blue'
}

export default defineComponent({
  name: 'OfficeRequisicaoCompraTab',
  setup() {
    const rcs = ref<Rc[]>([])
    const suppliers = ref<Supplier[]>([])
    const loading = ref(false)
    const mode = ref<'list' | 'view' | 'form'>('list')
    const editId = ref('')
    const form = ref(emptyForm())
    const formError = ref('')
    const formItemsLoading = ref(false)
    const saving = ref(false)
    const statusFilter = ref('')
    const viewRc = ref<Rc | null>(null)
    const viewItems = ref<RcItem[]>([])
    const viewLoading = ref(false)
    const lightboxUrl = ref('')

    const projects = computed(() => projectsStore.all())

    const statusFilters = [
      { value: '', label: 'Todas' },
      { value: 'rascunho', label: 'Rascunho' },
      { value: 'pendente', label: 'Pendente' },
      { value: 'aprovada', label: 'Aprovada' },
      { value: 'reprovada', label: 'Reprovada' },
      { value: 'concluida', label: 'Concluída' }
    ]

    const filteredRcs = computed(() =>
      statusFilter.value ? rcs.value.filter(r => r.status === statusFilter.value) : rcs.value
    )

    const viewItemsTotal = computed(() =>
      viewItems.value.reduce((sum, it) => sum + (Number(it.valorUnitarioEstimado) || 0) * (Number(it.quantidade) || 1), 0)
    )

    const canEdit = (rc: Rc) => ['rascunho', 'pendente'].includes(rc.status)

    const load = async () => {
      loading.value = true
      try {
        const [rcData, supData] = await Promise.allSettled([
          apiFetch<Rc[]>('/office/purchase-requests'),
          apiFetch<Supplier[]>('/office/suppliers')
        ])
        if (rcData.status === 'fulfilled') rcs.value = rcData.value
        if (supData.status === 'fulfilled') suppliers.value = supData.value
      } finally { loading.value = false }
    }

    const fetchItems = async (rcId: string): Promise<RcItem[]> => {
      const raw = await apiFetch<Partial<RcItem>[]>(`/office/purchase-requests/${rcId}/items`)
      return raw.map(it => ({
        nome: it.nome || '',
        descricao: it.descricao || '',
        link: it.link || '',
        imageUrl: it.imageUrl || '',
        quantidade: Number(it.quantidade || 1),
        unidade: it.unidade || 'un',
        valorUnitarioEstimado: Number(it.valorUnitarioEstimado || 0),
        warehouseItemId: it.warehouseItemId || ''
      }))
    }

    const openView = async (rc: Rc) => {
      viewRc.value = rc
      mode.value = 'view'
      viewLoading.value = true
      viewItems.value = []
      try { viewItems.value = await fetchItems(rc.id) }
      catch { uiStore.toast('Erro ao carregar itens da requisição.', 'error') }
      finally { viewLoading.value = false }
    }

    const backToList = () => { mode.value = 'list'; viewRc.value = null; viewItems.value = [] }

    const refreshView = async (id: string) => {
      await load()
      const updated = rcs.value.find(r => r.id === id)
      if (updated && mode.value === 'view') viewRc.value = updated
    }

    const openForm = async (rc?: Rc) => {
      formError.value = ''
      if (rc) {
        editId.value = rc.id
        form.value = { titulo: rc.titulo, justificativa: rc.justificativa, urgencia: rc.urgencia, projectId: rc.projectId, fornecedorId: rc.fornecedorId, items: [] }
        mode.value = 'form'
        formItemsLoading.value = true
        try { form.value.items = await fetchItems(rc.id) }
        catch { uiStore.toast('Erro ao carregar itens da requisição.', 'error') }
        finally { formItemsLoading.value = false }
      } else {
        editId.value = ''; form.value = emptyForm(); mode.value = 'form'
      }
    }
    const closeForm = () => {
      formError.value = ''; editId.value = ''
      // se veio da visualização, volta para ela; senão, para a lista
      mode.value = viewRc.value ? 'view' : 'list'
    }

    const addItem = () => form.value.items.push({ nome: '', descricao: '', link: '', imageUrl: '', quantidade: 1, unidade: 'un', valorUnitarioEstimado: 0 })
    const removeItem = (idx: number) => form.value.items.splice(idx, 1)

    const save = async () => {
      if (!form.value.titulo.trim()) { formError.value = 'Título é obrigatório.'; return }
      saving.value = true; formError.value = ''
      const payload = {
        titulo: form.value.titulo,
        justificativa: form.value.justificativa,
        urgencia: form.value.urgencia,
        projectId: form.value.projectId,
        fornecedorSugeridoId: form.value.fornecedorId,
        items: form.value.items
      }
      try {
        const savedId = editId.value
        if (savedId) await apiFetch(`/office/purchase-requests/${savedId}`, { method: 'PUT', body: JSON.stringify(payload) })
        else await apiFetch('/office/purchase-requests', { method: 'POST', body: JSON.stringify(payload) })
        uiStore.toast(savedId ? 'RC atualizada.' : 'RC criada.', 'success')
        editId.value = ''; formError.value = ''
        await load()
        const updated = savedId && viewRc.value && savedId === viewRc.value.id
          ? rcs.value.find(r => r.id === savedId)
          : null
        if (updated) await openView(updated)
        else { mode.value = 'list'; viewRc.value = null }
      } catch (e: unknown) { formError.value = e instanceof Error ? e.message : 'Erro ao salvar.' }
      finally { saving.value = false }
    }

    const submit = async (id: string) => {
      try {
        await apiFetch(`/office/purchase-requests/${id}/submit`, { method: 'POST', body: JSON.stringify({}) })
        uiStore.toast('RC enviada para aprovação.', 'success')
        await refreshView(id)
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro.', 'error') }
    }

    const respond = async (id: string, action: 'approve' | 'reject') => {
      try {
        await apiFetch(`/office/purchase-requests/${id}/respond`, { method: 'POST', body: JSON.stringify({ action }) })
        uiStore.toast(action === 'approve' ? 'RC aprovada.' : 'RC reprovada.', 'success')
        await refreshView(id)
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro.', 'error') }
    }

    const openImage = (url: string) => { lightboxUrl.value = resolveUrl(url) }

    const rcStatusLabel = (s: string) => RC_STATUS_LABELS[s] || s
    const rcStatusClass = (s: string) => RC_STATUS_CLASS[s] || ''
    const formatDate = (d: string) => {
      const m = `${d || ''}`.match(/(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[3]}/${m[2]}/${m[1]}` : d
    }
    const formatCurrency = (v: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

    const exporting = ref(false)
    const exportToExcel = async () => {
      exporting.value = true
      try {
        const data = await apiFetch<RcExport[]>('/office/purchase-requests-export')
        if (!data.length) { uiStore.toast('Nenhuma requisição para exportar.', 'error'); return }
        const header = ['Data', 'Título', 'Status', 'Urgência', 'Obra', 'Fornecedor sugerido', 'Solicitante', 'Origem', 'Item', 'Qtd', 'Un.', 'Descrição do item', 'Link']
        const rows: (string | number)[][] = [header]
        for (const rc of data) {
          const base = [
            formatDate(rc.createdAt),
            rc.titulo,
            rcStatusLabel(rc.status),
            rc.urgencia,
            rc.projectName || '',
            rc.fornecedorNome || '',
            rc.requestedByName || '',
            rc.sourceSubmissionId ? 'Relatório de campo' : 'Manual'
          ]
          if (!rc.items.length) {
            rows.push([...base, '', '', '', '', ''])
            continue
          }
          for (const it of rc.items) {
            rows.push([
              ...base,
              it.nome || it.descricao || '',
              Number(it.quantidade || 1),
              it.unidade || 'un',
              it.nome ? (it.descricao || '') : '',
              it.link || ''
            ])
          }
        }
        const workbook = XLSX.utils.book_new()
        const sheet = XLSX.utils.aoa_to_sheet(rows)
        sheet['!cols'] = [{ wch: 11 }, { wch: 36 }, { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 28 }, { wch: 6 }, { wch: 6 }, { wch: 36 }, { wch: 32 }]
        XLSX.utils.book_append_sheet(workbook, sheet, 'Requisicoes')
        const content = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
        const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `requisicoes-compra-${new Date().toISOString().slice(0, 10)}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e: unknown) {
        uiStore.toast(e instanceof Error ? e.message : 'Erro ao exportar.', 'error')
      } finally { exporting.value = false }
    }

    onMounted(() => {
      load()
      if (!projectsStore.all().length) projectsStore.refresh().catch(() => { /* ignore */ })
    })

    return {
      rcs, suppliers, projects, loading, mode, editId, form, formError, formItemsLoading, saving,
      statusFilter, statusFilters, filteredRcs, viewRc, viewItems, viewLoading, viewItemsTotal,
      lightboxUrl, canEdit, openView, backToList, openForm, closeForm, addItem, removeItem,
      save, submit, respond, openImage, resolveUrl, rcStatusLabel, rcStatusClass, formatDate, formatCurrency,
      exporting, exportToExcel
    }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 13px; font-weight: 600;
  &:hover { opacity: .88; }
}
.tab-head-actions { display: flex; gap: 8px; align-items: center; }
.btn-secondary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 8px; cursor: pointer;
  background: #fff; color: var(--ink, #2c2b27);
  border: 1.5px solid rgba(31,30,26,.18);
  font-size: 13px; font-weight: 600;
  &:hover:not(:disabled) { background: rgba(31,30,26,.04); }
  &:disabled { opacity: .6; cursor: not-allowed; }
}
.origin-chip {
  display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 20px;
  background: #ede9fe; color: #6d28d9;
}
.filter-tabs { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 20px; }
.filter-tab {
  padding: 6px 13px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.12);
  background: #fff; cursor: pointer; font-size: 12px; font-weight: 500; color: var(--ink-muted, #666);
  transition: all .12s;
  &:hover { background: rgba(31,30,26,.04); }
  &.active { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); border-color: transparent; }
}

/* ===== Tela cheia (visualizar / editar) =====
   Fixa entre a TopNavBar (topo) e a SideNavBar (esquerda), cobrindo
   toda a área de conteúdo — inclusive as abas do Escritório. */
.page-shell {
  position: fixed;
  top: var(--navbar-h, 48px);
  left: 240px;
  right: 0;
  bottom: 0;
  z-index: 40; /* abaixo da sidebar mobile (50) e do backdrop (49) */
  background: var(--bg, #f5f4f1);
  overflow-y: auto;
  padding: 16px 28px 32px;
}
.page-topbar { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
.page-topbar-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.btn-back {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: 8px; cursor: pointer;
  background: transparent; border: none;
  font-size: 13px; font-weight: 600; color: var(--ink-muted, #666);
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
}
.btn-approve {
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer;
  background: #15803d; color: #fff; font-size: 13px; font-weight: 600;
  &:hover { opacity: .88; }
}
.btn-reject {
  padding: 8px 16px; border-radius: 8px; border: 1.5px solid #fecaca; cursor: pointer;
  background: #fff; color: #dc2626; font-size: 13px; font-weight: 600;
  &:hover { background: #fef2f2; }
}
.page-card { width: 100%; }
.view-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
.view-title { margin: 0; font-size: 20px; font-weight: 800; color: var(--ink, #2c2b27); }
.form-page-title { margin-bottom: 18px; }
.view-chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.meta-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 14px;
  padding: 14px 16px; border: 1px solid rgba(31,30,26,.08); border-radius: 10px;
  background: rgba(31,30,26,.02); margin-bottom: 20px;
}
.meta-block { display: flex; flex-direction: column; gap: 3px; }
.meta-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #999); text-transform: uppercase; letter-spacing: .02em; }
.meta-value { font-size: 13px; color: var(--ink, #2c2b27); }
.meta-value--strong { font-weight: 700; }
.view-section { margin-bottom: 20px; &:last-child { margin-bottom: 0; } }
.view-section-title { margin: 0 0 8px; font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); }
.view-text { margin: 0; font-size: 13px; line-height: 1.55; color: var(--ink, #2c2b27); white-space: pre-wrap; }
.items-table-wrap { overflow-x: auto; border: 1px solid rgba(31,30,26,.08); border-radius: 10px; }
.items-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
  th {
    text-align: left; font-size: 11px; font-weight: 700; color: var(--ink-muted, #888);
    text-transform: uppercase; letter-spacing: .02em;
    padding: 9px 12px; border-bottom: 1px solid rgba(31,30,26,.1); background: rgba(31,30,26,.02);
  }
  td { padding: 9px 12px; border-bottom: 1px solid rgba(31,30,26,.06); color: var(--ink, #2c2b27); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tfoot td { border-top: 1px solid rgba(31,30,26,.1); border-bottom: none; background: rgba(31,30,26,.02); }
  .col-num { text-align: right; white-space: nowrap; }
  th.col-num { text-align: right; }
  .col-img { width: 44px; padding-right: 0; }
  .cell-strong { font-weight: 700; }
}
.item-thumb { width: 34px; height: 34px; object-fit: cover; border-radius: 6px; cursor: zoom-in; display: block; }
.item-link { color: var(--system-navbar, #0f4c81); font-weight: 600; text-decoration: underline; }
.lightbox {
  position: fixed; inset: 0; z-index: 1000; background: rgba(0,0,0,.75);
  display: flex; align-items: center; justify-content: center; cursor: zoom-out;
  img { max-width: 90vw; max-height: 90vh; border-radius: 10px; }
}

/* ===== Formulário ===== */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field--full { grid-column: 1 / -1; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input {
  padding: 8px 11px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18);
  font-size: 13px; outline: none; background: #fff;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.field-textarea { resize: vertical; }
.items-section { border: 1px solid rgba(31,30,26,.1); border-radius: 10px; padding: 12px; margin-bottom: 16px; }
.items-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.items-title { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); }
.btn-add-item { padding: 5px 12px; border-radius: 6px; border: 1.5px solid rgba(31,30,26,.15); background: #fff; cursor: pointer; font-size: 12px; font-weight: 600; &:hover { background: rgba(31,30,26,.04); } }
.item-row { display: grid; grid-template-columns: 1.2fr 1.4fr 1fr 64px 56px 96px 28px; gap: 6px; margin-bottom: 8px; align-items: center; }
.item-row--head {
  margin-bottom: 4px;
  span { font-size: 10px; font-weight: 700; color: var(--ink-muted, #999); text-transform: uppercase; letter-spacing: .02em; padding: 0 3px; }
}
.item-desc, .item-qty, .item-unit, .item-value { padding: 6px 8px; border-radius: 6px; border: 1.5px solid rgba(31,30,26,.15); font-size: 12px; outline: none; &:focus { border-color: var(--system-navbar, #0f4c81); } }
.btn-remove-item { background: none; border: none; cursor: pointer; color: var(--ink-muted, #aaa); font-size: 14px; padding: 0; &:hover { color: #dc2626; } }
.items-empty-hint { font-size: 12px; color: var(--ink-muted, #aaa); text-align: center; padding: 8px 0; }
.form-error { font-size: 12px; color: #dc2626; margin: 8px 0 0; }
.form-actions { display: flex; gap: 8px; }
.btn-save { padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer; background: var(--system-navbar, #0f4c81); color: #fff; font-size: 13px; font-weight: 600; &:disabled { opacity: .6; cursor: not-allowed; } &:hover:not(:disabled) { opacity: .88; } }
.btn-cancel { padding: 8px 14px; border-radius: 7px; border: 1.5px solid rgba(31,30,26,.18); background: #fff; cursor: pointer; font-size: 13px; font-weight: 500; &:hover { background: rgba(31,30,26,.04); } }
.empty-hint { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px; svg { opacity: .35; } }

/* ===== Cards (maiores e clicáveis) ===== */
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
.rc-card {
  border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card, #fff);
  display: flex; flex-direction: column; cursor: pointer;
  transition: box-shadow .15s, border-color .15s, transform .15s;
  &:hover { box-shadow: 0 4px 16px rgba(31,30,26,.1); border-color: rgba(31,30,26,.18); transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid var(--system-navbar, #0f4c81); outline-offset: 2px; }
}
.card-body { padding: 18px 20px 12px; flex: 1; display: flex; flex-direction: column; gap: 5px; }
.card-header-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.card-title { font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.card-meta { font-size: 12px; color: var(--ink-muted, #888); }
.card-justificativa {
  margin: 2px 0 0; font-size: 12px; color: var(--ink-muted, #777); line-height: 1.45;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.card-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 6px; }
.card-value { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); margin-left: auto; }
.urgencia-badge {
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 20px; flex-shrink: 0;
  &.urgencia--normal { background: #f0f4ff; color: #3b5bdb; }
  &.urgencia--alta { background: #ffedd5; color: #c2410c; }
  &.urgencia--urgente { background: #fef2f2; color: #dc2626; }
}
.status-chip {
  display: inline-block; font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 20px;
  &.chip--draft { background: #f4f4f2; color: var(--ink-muted, #666); }
  &.chip--yellow { background: #fef9c3; color: #854d0e; }
  &.chip--green { background: #dcfce7; color: #15803d; }
  &.chip--red { background: #fef2f2; color: #dc2626; }
  &.chip--blue { background: #dbeafe; color: #1d4ed8; }
}
.card-footer { display: flex; gap: 4px; padding: 8px 12px; border-top: 1px solid rgba(31,30,26,.07); flex-wrap: wrap; cursor: default; }
.card-btn {
  display: inline-flex; align-items: center; gap: 5px; padding: 6px 10px; border-radius: 6px; border: none; cursor: pointer;
  background: transparent; color: var(--ink-muted, #666); font-size: 11.5px; font-weight: 500; transition: background .12s;
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  &--primary { color: var(--system-navbar, #0f4c81); &:hover { background: #f0f5ff; } }
  &--ok { color: #15803d; &:hover { background: #dcfce7; } }
  &--warn { color: #c2410c; &:hover { background: #fff7ed; } }
}
@media (max-width: 900px) {
  .page-shell { left: 0; padding: 14px 16px 28px; }
}
@media (max-width: 720px) {
  .form-grid { grid-template-columns: 1fr; }
  .item-row { grid-template-columns: 1fr 1fr; }
  .item-row--head { display: none; }
}
</style>
